import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { facilityService } from '../../services/facilityService';
import { FacilityCard } from './FacilityCard';
import { FacilityFormModal } from './FacilityFormModal';
import { ConfirmationModal } from '../common/ConfirmationModal';
import type { Facility } from '../../types/facility';

interface FacilitiesSectionProps {
  businessProfileId: string;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

export function FacilitiesSection({
  businessProfileId,
  isCollapsed = false,
  onToggle,
}: FacilitiesSectionProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [deletingFacility, setDeletingFacility] = useState<Facility | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadFacilities = useCallback(async () => {
    if (!token || !businessProfileId) return;

    setIsLoading(true);
    try {
      const data = await facilityService.getFacilitiesByBusinessProfile(businessProfileId, token);
      setFacilities(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load facilities';
      showToast('error', t('common.error'), message);
    } finally {
      setIsLoading(false);
    }
  }, [token, businessProfileId, showToast, t]);

  // Load data on mount to show count in header
  useEffect(() => {
    loadFacilities();
  }, [loadFacilities]);

  const handleAdd = () => {
    setEditingFacility(null);
    setIsFormOpen(true);
  };

  const handleEdit = (facility: Facility) => {
    setEditingFacility(facility);
    setIsFormOpen(true);
  };

  const handleDelete = (facility: Facility) => {
    setDeletingFacility(facility);
  };

  const confirmDelete = async () => {
    if (!token || !deletingFacility) return;

    setIsDeleting(true);
    try {
      await facilityService.deleteFacility(deletingFacility.id, token);
      showToast('success', t('facilities.deleteSuccess'));
      setFacilities(prev => prev.filter(f => f.id !== deletingFacility.id));
      setDeletingFacility(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete facility';
      showToast('error', t('common.error'), message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingFacility(null);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingFacility(null);
    loadFacilities();
  };

  return (
    <div className="bg-brand-primary-deep rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-brand-primary-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <span className="text-lg font-medium text-white">{t('facilities.title')}</span>
          <span className="text-sm text-white/70">({facilities.length})</span>
        </div>
        <svg
          className={`w-5 h-5 text-white/70 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-4">
          {/* Add Button */}
          <button
            onClick={handleAdd}
            className="w-full py-3 border-2 border-dashed border-white/30 rounded-lg text-white/80 hover:border-brand-primary-light hover:text-brand-primary-light transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('facilities.addFacility')}
          </button>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="bg-white/10 rounded-lg p-4 animate-pulse">
                  <div className="h-5 bg-white/20 rounded w-48 mb-2" />
                  <div className="h-4 bg-white/20 rounded w-32" />
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && facilities.length === 0 && (
            <div className="text-center py-8 text-white/60">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p>{t('facilities.noFacilities')}</p>
            </div>
          )}

          {/* Facilities List */}
          {!isLoading && facilities.length > 0 && (
            <div className="space-y-3">
              {facilities.map(facility => (
                <FacilityCard
                  key={facility.id}
                  facility={facility}
                  onEdit={() => handleEdit(facility)}
                  onDelete={() => handleDelete(facility)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <FacilityFormModal
          facility={editingFacility}
          businessProfileId={businessProfileId}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingFacility && (
        <ConfirmationModal
          title={t('facilities.deleteConfirmTitle')}
          message={t('facilities.deleteConfirmMessage', { name: deletingFacility.name })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          onConfirm={confirmDelete}
          onCancel={() => setDeletingFacility(null)}
          isLoading={isDeleting}
          variant="danger"
        />
      )}
    </div>
  );
}
